/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { MdOutlineVisibility as RevealIcon, MdOutlineVisibilityOff as HideIcon } from "react-icons/md";
import { useFormikContext } from "formik";
import { ScrollToFormikError } from "../elements/ScrollToFormikError";
// import "intl-tel-input/build/css/intlTelInput.css";
// import intlTelInput from "intl-tel-input";

interface IInputProps {
    id?: string;
    label?: string;
    description?: string;
    disabled?: boolean;
    prepend?: JSX.Element | React.ReactElement | string | number | any;
    append?: JSX.Element | React.ReactElement | string | number | any;
    type?: "text" | "number" | "email" | "tel" | "password" | "search" | "checkbox" | "radio" | "date" | "file"| "url";
    name?: string;
    // countryCode?: boolean;
    // defaultCountryCode?: "gh"
    placeholder?: string;
    min?: number;
    max?: number;
    value?: string | number;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void | null | any;
    onBlur?: (e: React.FocusEvent<any, Element>) => void | null | any;
    error?: string | undefined;
    required?: boolean;
    helperText?: string;
    autoComplete?: boolean;
    className?: string;
    wrapperClasses?: string;
    showPasswordIcon?: boolean;
    wiggle?: boolean;
    readOnly?: boolean, 
    prependClasses?: string;
    pattern?: string;
    step?: string;
}

function Input({
    id,
    label,
    description,
    disabled = false,
    prepend,
    append,
    type = "text",
    name,
    placeholder,
    readOnly = false,
    min,
    max,
    value,
    onChange,
    onBlur,
    error,
    required = false,
    helperText,
    autoComplete = false,
    className,
    wrapperClasses,
    showPasswordIcon = false,
    // countryCode = false,
    wiggle = true,
    prependClasses, 
    pattern, 
    step
}: IInputProps): JSX.Element {
    // const inputRef = createRef<HTMLInputElement>();
    const [showPassword, setShowPassword] = useState(false);
    const [currentType, setCurrentType] = useState(type);
    const fieldRef = useRef<HTMLInputElement>(null);

    // useEffect(() => {
    //     if(countryCode && type == "tel") {
    //         if(inputRef.current) {
    //             const input = intlTelInput(inputRef.current, {
    //                 utilsScript: "/js/intl-tel-input-utils.min.js",
    //                 customContainer: "w-full",
    //                 onlyCountries: ["gh"],
    //                 initialCountry: "gh",
    //                 // preferredCountries: ["gh"],
    //                 // separateDialCode: true
    //             });
    //         }
    //     }
    // }, [countryCode, type]);

    const toggleRevealHidePassword = () => {
        setCurrentType(currentType == "password" ? "text" : "password");
        setShowPassword(!showPassword)
    }

    return (
        <div className={clsx(
            "w-auto",
            { ["animate-wiggle"]: error && required },
            wrapperClasses
        )}>
            {/* Label */}
            {label && (
                <label
                    htmlFor={id}
                    className={clsx(
                        "block text-sm font-medium text-[#004E60] dark:text-text-dark-primary",
                        { ["text-danger-main"]: error && required }
                    )}
                >
                    {label} <span className="text-danger-main">{required && "*"}</span>
                    {description && (
                        <p className={clsx(
                            "text-xs text-gray-400 dark:text-text-dark-secondary italic",
                            { ["text-danger-border"]: error && required }
                        )}>
                            {description}
                        </p>
                    )}
                </label>
            )}

            <div className={clsx(
                "relative",
                { ["mt-1"]: Boolean(label) }
            )}>
                <div className="relative w-auto">
                    <input
                        ref={fieldRef}
                        type={currentType}
                        name={name}
                        id={id}
                        readOnly={readOnly}
                        onChange={onChange}
                        onBlur={onBlur}
                        className={clsx(
                            "relative border border-gray-300 dark:border-primary-dark-border_active shadow-sm text-left cursor-default rounded",
                            "focus:outline-none focus:ring-1 focus:ring-primary-surface focus:dark:ring-primary-dark-border_active focus:border-primary-main focus:dark:border-primary-dark-border_active sm:text-sm",
                            "flex items-center placeholder:text-gray-400 dark:text-text-dark-primary",
                            type === "checkbox" ? "h-4 w-4 rounded-sm text-primary-main" : "w-full py-2",
                            type !== "checkbox" ? prepend ? "pl-12" : "pl-3" : null,
                            type !== "checkbox" ? append ? "pr-10" : "pr-3" : null,
                            disabled ? "bg-gray-300 dark:bg-primary-dark-secondary cursor-not-allowed" : "bg-white dark:bg-primary-dark-main",
                            { ["bg-red-200 !border-danger-main focus:ring-danger-main focus:border-danger-border"]: error && required },
                            error && "!border-danger-main",
                            className,
                            { ["animate-wiggle"]: Boolean(error) && wiggle },
                            readOnly && "border-none"
                        )}
                        placeholder={placeholder}
                        disabled={disabled}
                        min={min}
                        max={max}
                        value={value}
                        autoComplete={autoComplete ? undefined : String(autoComplete)}
                        pattern={pattern}
                        step={step}
                    />

                    {prepend && (
                        <div className={clsx("absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none w-12 sm:w-15 h-full text-[#004E60]", prependClasses)}>
                            {prepend}
                        </div>
                    )}

                    {type == "password" && showPasswordIcon ? (
                        <div className="absolute inset-y-0 right-0 p-3 flex items-center cursor-pointer text-[#004E60]">
                            {showPassword ? (
                                <HideIcon className="h-5 w-5" onClick={toggleRevealHidePassword} />
                            ) : (
                                <RevealIcon className="h-5 w-5" onClick={toggleRevealHidePassword} />
                            )}
                        </div>
                    ) : (
                        <>
                            {append && (
                                <div className="absolute inset-y-0 right-0 pr-1 flex items-center text-[#004E60]">
                                    {append}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Helper Text */}
            <div className={clsx("text-xs text-gray-400 dark:text-text-dark-secondary", { ["!text-danger-main"]: error })}>{helperText}</div>
            <ScrollToFormikError 
                fieldName={name??id??""} 
                fieldRef={fieldRef}
            />
        </div>
    )
}

export default Input;
